import styles from './DoctorSelector.module.scss';

import * as React from 'react';

import { MessageBar, MessageBarType, NormalPeoplePicker, PersonaSize } from '@fluentui/react';
import { useCallback, useMemo } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { fetchPreferredDoctors } from '../../api/patients';
import { PreferredDoctor } from '../../api/patients.types';

function convert(p: Partial<PreferredDoctor>): { text: string; imageUrl: string; id: string } {
  return {
    text: `${p.prefix} ${p.firstName} ${p.lastName}`,
    imageUrl: p.avatarUrl!,
    id: p.providerId!,
  };
}

export const PreferredDoctorSelector = ({ patientId, doctor, onChange }: { patientId: string; doctor: Partial<PreferredDoctor>; onChange: (id: string) => void }) => {
  const [providers, setProviders] = useState<Partial<PreferredDoctor>[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPreferredDoctors(patientId).then(
      (value) => {
        if (
          doctor.providerId &&
          !value.find((availableDoctor) => {
            return availableDoctor.providerId === doctor.providerId;
          })
        ) {
          // If currently selected doctor is not in the list, add it. This will allow deletion.
          const allProviders = [doctor, ...value];
          setProviders(allProviders);
        } else {
          setProviders(value);
        }
      },
      (error) => {
        if (error.message.indexOf('home address') > -1) {
          setError('Patient needs a home address in order to select a Preferred Doctor');
        } else {
          setError('Could not retrieve the list of available Preferred Doctors');
        }
      }
    );
    // Intentional to not have doctor in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const getSuggestions = useCallback(() => {
    return providers.map((p) => ({
      ...convert(p),
      size: PersonaSize.size32,
    }));
  }, [providers]);

  const selectedItems = useMemo(() => {
    const provider = providers.find((p) => p.providerId === doctor.providerId);
    if (provider) {
      return [convert(provider)];
    }
    return [];
  }, [providers, doctor]);

  const onFilterChange = useCallback(
    (filterText: string) => {
      if (!filterText) {
        return [];
      }
      return providers
        .filter((p) => p.firstName!.toLowerCase().indexOf(filterText.toLowerCase()) === 0 || p.lastName!.toLowerCase().indexOf(filterText.toLowerCase()) === 0)
        .map(convert);
    },
    [providers]
  );

  const onPickerChange = useCallback(
    (items?: any[]) => {
      if (items && items.length > 0) {
        onChange(items[0].id);
      } else {
        onChange('');
      }
    },
    [onChange]
  );

  return (
    <div>
      <NormalPeoplePicker
        onResolveSuggestions={onFilterChange}
        onEmptyResolveSuggestions={getSuggestions}
        key="controlled"
        selectedItems={selectedItems}
        pickerSuggestionsProps={{ className: styles.item }}
        onChange={onPickerChange}
        itemLimit={2}
      />
      {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
    </div>
  );
};
